import { CLOSE_SOURCE, Modal, ROLE } from 'baseui/modal';
import React from 'react';
import styled from 'styled-components';
import { FileUploader } from '../../../../Components/FileUploader';
import { FinalButton } from '../../../../Components/FinalButton';
import { Notification } from '../../../../Components/Notification';
import { UploadProgress } from '../../../../Components/Upload/UploadProgress';
import { Modal as MantraModal, Text } from '../../../../globalStyles';
import { ACCEPTED_TYPES } from './constants';
import { useProspectUploadLogic } from './hooks/useProspectUploadLogic';

type SurveyProspectUploadModalProps = {
  isOpen: boolean;
  surveyId: number;
  surveyName: string;
  organizationName: string;
  onClose: () => void;
  /** Fired after the list is replaced, so the caller can refetch its summary. */
  onUpdated: (prospectCount: number) => void;
};

const HEADER_ID = 'survey-prospect-upload-modal-header';

const HELPER_TEXT =
  'Upload a CSV file containing the list of students to be surveyed. An "email" column is ' +
  'required. Add an optional "ssoUniqueId" column to match students to their SSO accounts, and an ' +
  'optional "cohort" column to group them. Every email must be on one of this organization\'s ' +
  'approved domains.';

export const SurveyProspectUploadModal = ({
  isOpen,
  surveyId,
  surveyName,
  organizationName,
  onClose,
  onUpdated,
}: SurveyProspectUploadModalProps) => {
  const { state, onDropAccepted, onDropRejected, commit, reset } = useProspectUploadLogic({
    surveyId,
    onCommitted: onUpdated,
  });

  const isCommitting = state.status === 'committing';
  const isUploading = state.status === 'uploading';
  // The dropzone gives way to the progress row as soon as a file is accepted.
  const showDropzone = state.status === 'idle' || state.status === 'error';

  const progress = (() => {
    if (state.status === 'uploading') return state.progress;
    if (state.status === 'staged' || isCommitting) return 100;
    return undefined;
  })();

  const fileName = 'fileName' in state ? state.fileName : undefined;

  /**
   * A stray click on the backdrop must not discard an uploaded file that is
   * sitting there waiting to commit.
   */
  const handleClose = ({ closeSource }: { closeSource?: CLOSE_SOURCE[keyof CLOSE_SOURCE] }) => {
    if (closeSource === CLOSE_SOURCE.backdrop) return;
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      closeable={!isCommitting}
      role={ROLE.dialog}
      overrides={{ Dialog: { props: { 'aria-labelledby': HEADER_ID } } }}
      unstable_ModalBackdropScroll
    >
      <MantraModal.body>
        <Stack>
          <Intro>
            <MantraModal.header id={HEADER_ID}>Update Participants List</MantraModal.header>
            <Text.bodySmall className="db">
              <strong>Survey Period:</strong> {surveyName}
            </Text.bodySmall>
            <Text.bodySmall className="db">
              <strong>Org:</strong> {organizationName}
            </Text.bodySmall>
            <Text.bodySmall className="mt3 db">{HELPER_TEXT}</Text.bodySmall>
          </Intro>

          {state.status === 'error' && (
            <Notification kind="negative">
              <ErrorBody>
                <Text.bodySmallBold>{state.message}</Text.bodySmallBold>
                {state.detail && <Text.bodySmall>{state.detail}</Text.bodySmall>}
              </ErrorBody>
            </Notification>
          )}

          {showDropzone ? (
            <FileUploader
              accept={ACCEPTED_TYPES}
              dropPrompt="Drop a file here or"
              onDropAccepted={onDropAccepted}
              onDropRejected={onDropRejected}
            />
          ) : (
            <UploadProgress
              progress={progress}
              fileName={fileName}
              onAbandon={isCommitting ? undefined : reset}
              onCancel={isUploading ? reset : undefined}
            />
          )}

          {state.status === 'staged' && (
            <Text.bodySmall kind="grayText" className="db">
              Updating the list replaces every participant currently on this survey period. The file
              is checked as it is read — if anything in it is wrong, the current list is left alone.
            </Text.bodySmall>
          )}

          <FinalButton
            className="w-100"
            kind="primary"
            loading={isCommitting}
            disabled={state.status !== 'staged'}
            onClick={commit}
          >
            Update List
          </FinalButton>
        </Stack>
      </MantraModal.body>
    </Modal>
  );
};

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Intro = styled.div`
  display: flex;
  flex-direction: column;
`;

const ErrorBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  overflow-wrap: anywhere;
  word-break: break-word;
`;
